import {Component, h} from 'preact'
import {GenericHomepageElementTypes, HomepageElement} from '../homepage'
import {Hero} from '../hero'
import TagCloud from '../tag-cloud'
import {HTMLWidget} from '../html-widget/HTMLWidget'
import {QuickLinksManager} from '../quick-links/QuickLinksManager'
import {TabsConfigurator} from '../homepage-tabs/TabsConfigurator'
import {FeaturedTopics} from '../featured-topics/FeaturedTopics'
import FeatureTopicsModeratorTagsWithHelp from '../homepage/FeaturedTopicsModeratorTags'
import {BadgesConfigurator} from '../widget-badges/configurator'
import {SolvedTopicsConfigurator} from '../widget-solved-topics/configurator'
import {MostLikedConfigurator} from '../widget-most-liked/configurator'
import CommunityStatisticsConfigurator from '../widget-community-statistics/configurator'
import Portal from 'preact-portal'
import TextButton from '../ui-components/TextButton'
import Icon from '../assets'
import Button from '../ui-components/Button'
import {HomepageContentCurationManager} from '../content-curation/HomepageContentCurationManager'
import LeaderboardPointsConfig from '../widget-leaderboard-points-config'
import FeaturedSidebarWithHelp from '../widget-notification/featuredSidebar'
import {ElementChange} from './toolbar'
import CommunityCategoryWithHelp from '../community-category/CommunityCategoryWithHelp'
import KnowledgeBaseWithHelp from '../knowledge-base/KnowledgeBaseWithHelp'
import EventCalendarConfigurator from '../widget-events-calendar/configurator'
import GroupsWidgetConfigurator from '../widget-groups/configurator'
import PipelineConfigurator from '../ideation-destination/PipelineConfigurator'
import { HeroWithHelp } from '../ideation-destination/hero'

type TypeConfigElement = Pick<HomepageElement, 'type' | 'config' | 'title'>

interface Props {
  homepageElement: HomepageElement
  onChange: (config: TypeConfigElement) => void
  onDone: (config: TypeConfigElement) => void
  onClose: (initialConfig: TypeConfigElement) => void
  widgetTitle: string
  error: string
  isVisible: boolean
  controlUrl: string
}

interface State {
  isVisible: boolean,
  isOpen: boolean,
}

export default class WidgetConfigurator extends Component<Props, State> {
  newConfig: TypeConfigElement
  initialConfig: TypeConfigElement
  timeout: number = 0
  changeTimeout: number = 0

  constructor (props: Props) {
    super()
    this.newConfig = this.initialConfig = {
      title: props.homepageElement.title,
      type: props.homepageElement.type,
      config: props.homepageElement.config,
    }
    this.onClose = this.onClose.bind(this)
    this.state = {
      isVisible: false,
      isOpen: false
    }
  }

  async done () {
    this.props.onDone(this.newConfig)
  }

  queueChange (config: ElementChange) {
    this.newConfig = config

    clearTimeout(this.changeTimeout)
    this.changeTimeout = setTimeout(() => {
      this.props.onChange(this.newConfig)
    }, 500)
  }

  getComponent (): JSX.Element | null {
    const el = this.props.homepageElement
    switch (el.type) {
      case 'hero_banner':
        return <Hero onChange={(config) => {
            this.queueChange({
              type: el.type,
              config: {
                heroImage: config.hero_image,
                headline: config.headline
              }
            })
          }} heroImage={el.config.heroImage} headline={el.config.headline} />
      case 'html_widget':
        return <HTMLWidget
          title={el.title}
          content={el.config.html}
          deleteButtonAction={() => {}}
          onChange={({ html, title }) => {
            this.queueChange({
              config: {
                html: html
              },
              type: el.type,
              title: title
            })
          }}
          hideDeleteButton />
      case 'knowledge_base':
        return <KnowledgeBaseWithHelp
          title={el.title}
          selectedTemplate={el.config.selectedTemplate || 0}
          controlUrl={this.props.controlUrl}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                selectedTemplate: config.selectedTemplate
              }
            })
          }}
        />
      case 'quick_links':
        return <QuickLinksManager
          isActive
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                quickLinks: config.quickLinks.map(link => ({
                  ...link,
                  iconSrc: link.iconSrc,
                  imageSrc: link.imageSrc,
                  openNewTab: link.openNewTab
                })),
                enableBorder: config.settings.enableBorder,
                borderRadius: config.settings.borderRadius
              }
            })
          }}
          title={el.title}
          quickLinks={el.config.quickLinks.map(link => ({
            ...link,
            icon_src: link.iconSrc,
            image_src: link.imageSrc,
            target: link.openNewTab
          }))}
          settings={{
            borderRadius: el.config.borderRadius,
            enableBorder: el.config.enableBorder,
          }}
        />
      case 'category_and_topics_stream':
      case 'category_and_topics_stream_only':
        return <TabsConfigurator
          tabs={el.config.tabs}
          class='tabs-configurator--wide'
          onChange={config => {
            this.queueChange({
              type: el.type,
              config: { tabs: config.tabs }
            })
          }}
        />
      case 'recommendations':
        return <HomepageContentCurationManager
          curatedTopics={el.config.curatedTopics}
          title={el.title}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: { curatedTopics: config.curatedTopics }
            })
          }}
        />
      case 'featured_topics':
        return <FeaturedTopics
          title={el.title}
          content={el.config}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                topics: config.topics,
                firstTopicsNumber: config.firstTopicsNumber,
                buttonLabel: config.buttonLabel,
                buttonURL: config.buttonURL
              }
            })
          }}
        />
      case 'leaderboard_points':
        return <LeaderboardPointsConfig
          title={el.title}
          content={el.config}
          controlUrl={this.props.controlUrl}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                maxResults: config.maxResults,
                excludeUserRole: config.excludeUserRole,
                timeRange: config.timeRange,
                emptyMessage: config.emptyMessage,
                isPreviewEmptyMessage: config.isPreviewEmptyMessage
              }
            })
          }}
        />
      case 'tag_cloud':
        return <TagCloud
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                amount: config.amount
              }
            })
          }}
        />
      case 'badges':
        return <BadgesConfigurator
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: { amount: config.amount }
            })
          }}
        />
      case 'event_calendar':
        return <EventCalendarConfigurator
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: { amount: config.amount }
            })
          }}
        />
      case 'groups':
        return <GroupsWidgetConfigurator
          title={el.title}
          selectedGroups={el.config.groups}
          buttonLabel={el.config.buttonLabel}
          buttonURL={el.config.buttonURL}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                groups: config.groups,
                buttonLabel: config.buttonLabel,
                buttonURL: config.buttonURL
              }
            })
          }}
        />
      case 'solved_topics':
        return <SolvedTopicsConfigurator
          title={el.title}
          amount={el.config.amount}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: { amount: config.amount }
            })
          }}
        />
      case 'most_liked':
        return <MostLikedConfigurator
          title={el.title}
          amount={el.config.amount}
          period={el.config.period}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                amount: config.amount,
                period: config.period
               }
            })
          }}
        />
      case 'community_statistics':
        return <CommunityStatisticsConfigurator
          title={el.title}
          showTotalTopics={el.config.total_topics}
          showTotalComments={el.config.total_comments}
          showTotalUsers={el.config.total_users}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                total_topics: config.showTotalTopics,
                total_comments: config.showTotalComments,
                total_users: config.showTotalUsers
              }
            })
          }}
        />
      case GenericHomepageElementTypes.feature_topics:
        return <FeatureTopicsModeratorTagsWithHelp />
      case GenericHomepageElementTypes.announcement_card:
        return <FeaturedSidebarWithHelp />
      case 'community_category':
        return <CommunityCategoryWithHelp
          title={el.title}
          selectedTemplate={el.config.selectedTemplate}
          controlUrl={this.props.controlUrl}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                selectedTemplate: config.selectedTemplate
              }
            })
          }}
        />
      case 'ideation_pipeline':
        return <PipelineConfigurator
          title={el.title}
          maxHeight={el.config.maxHeight}
          column_1={el.config.column_1}
          column_2={el.config.column_2}
          column_3={el.config.column_3}
          onChange={config => {
            this.queueChange({
              type: el.type,
              title: config.title,
              config: {
                column_1: config.column_1,
                column_2: config.column_2,
                column_3: config.column_3,
                maxHeight: config.maxHeight
              }
            })
          }}
        />
      case 'ideation_hero':
        return <HeroWithHelp onChange={(config) => {
          this.queueChange({
            type: el.type,
            config: {
              heroImage: config.hero_image,
              title: config.title,
              tagline: config.tagline
            }
          })
        }} heroImage={el.config.heroImage} title={el.config.title} tagline={el.config.tagline}
        />
      default:
        return null
    }
  }

  onClose () {
    this.props.onClose(this.initialConfig)
  }

  componentWillReceiveProps(newProps: Props) {
    if (newProps.isVisible === this.state.isVisible) return

    clearTimeout(this.timeout)
    if (newProps.isVisible) {
      this.setState({isVisible: true})
      this.timeout = setTimeout(() => {
        this.setState({isOpen: true})
      }, 1)
      return
    }

    this.setState({isOpen: false})
    this.timeout = setTimeout(() => {
      this.setState({isVisible: false})
    }, 400)
  }

  render ({ widgetTitle }: Props) {
    if (!this.state.isVisible) return null
    return <Portal into='body'>
      <div class='widget-configurator' style={{
        width: this.state.isOpen ? '400px' : 0
      }}>
        <div class='widget-configurator--header'>
          <h4  class='widget-configurator--header-title'>{widgetTitle}</h4>
          <TextButton style={{ padding: '4px', display: 'flex', borderRadius: '2px' }} class='close-btn ' onClick={this.onClose}>
            <Icon icon='close' />
          </TextButton>
        </div>
        <div class='box__pad widget-configurator--body widget-configurator__controls'>
          {this.props.error ? <div class="widget-configuration_error-box">
            {this.props.error}
          </div> : null}
          {this.getComponent()}
        </div>
        <div class='widget-configurator--footer'>
          <Button insided onClick={() => this.done()}>
            Done
          </Button>
        </div>
      </div>
    </Portal>
  }
}
